import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ExclamationCircleIcon,
  PhotoIcon,
  BoldIcon,
  ItalicIcon,
  ListBulletIcon,
  NumberedListIcon
} from '@heroicons/react/24/outline';
import { useNotices } from '../../contexts/NoticeContext';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Placeholder from '@tiptap/extension-placeholder';
import Image from '@tiptap/extension-image';
import { uploadImageToStorage } from '../../utils/imageUpload';

interface NoticeFormData {
  title: string;
  department: string;
  content: string;
  is_important: boolean;
  notice_end_date: string | null;
  is_published: boolean;
  author: string;
}

const NoticeForm: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { addNotice, updateNotice, getNotice } = useNotices();
  const isEditMode = !!id;

  const [formData, setFormData] = useState<NoticeFormData>({
    title: '',
    department: '',
    content: '',
    is_important: false,
    notice_end_date: null,
    is_published: true,
    author: '관리자'
  });

  const [errors, setErrors] = useState<Partial<Record<keyof NoticeFormData, string>>>({});
  const fileInputRef = useRef<HTMLInputElement>(null);

  const editor = useEditor({
    extensions: [
      StarterKit,
      Placeholder.configure({
        placeholder: '공지사항 내용을 입력하세요...',
      }),
      Image.configure({
        inline: true,
        allowBase64: false, // Supabase Storage 사용으로 변경
      }),
    ],
    content: formData.content,
    onUpdate: ({ editor }) => {
      setFormData({ ...formData, content: editor.getHTML() });
      if (errors.content) {
        setErrors({ ...errors, content: '' });
      }
    },
  });

  const handleImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && editor) {
      try {
        // Supabase Storage에 이미지 업로드
        const imageUrl = await uploadImageToStorage(file, 'notice-images');

        // 에디터에 이미지 URL 삽입
        editor.chain().focus().setImage({ src: imageUrl }).run();

        console.log('Notice image uploaded successfully:', imageUrl);
      } catch (error) {
        console.error('Failed to upload notice image:', error);
        alert('이미지 업로드에 실패했습니다. 다시 시도해주세요.');
      }
    }
  };

  const addImageFromButton = () => {
    fileInputRef.current?.click();
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
    if (errors[name as keyof NoticeFormData]) {
      setErrors({ ...errors, [name]: '' });
    }
  };

  const validateForm = () => {
    const newErrors: Partial<Record<keyof NoticeFormData, string>> = {};

    if (!formData.title) newErrors.title = '제목은 필수입니다.';
    if (!formData.department) newErrors.department = '담당부서는 필수입니다.';
    if (!formData.content) newErrors.content = '내용은 필수입니다.';

    // 마감기한이 입력된 경우에만 날짜 유효성 검사
    if (formData.is_important && formData.notice_end_date) {
      const endDate = new Date(formData.notice_end_date);
      const now = new Date();
      now.setHours(0, 0, 0, 0); // 오늘 날짜의 시작 시간으로 설정

      if (endDate < now) {
        newErrors.notice_end_date = '마감기한은 오늘 이후 날짜여야 합니다.';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      try {
        if (isEditMode && id) {
          await updateNotice(Number(id), formData);
          alert('공지사항이 성공적으로 수정되었습니다!');
        } else {
          await addNotice(formData);
          alert('공지사항이 성공적으로 등록되었습니다!');
        }
        navigate('/admin/notices');
      } catch (error) {
        console.error('Failed to submit notice:', error);
        alert('공지사항 저장에 실패했습니다.');
      }
    }
  };

  // Edit mode: Load existing notice data
  useEffect(() => {
    if (isEditMode && id) {
      const notice = getNotice(Number(id));
      if (notice) {
        setFormData({
          title: notice.title,
          department: notice.department,
          content: notice.content,
          is_important: notice.is_important,
          notice_end_date: notice.notice_end_date,
          is_published: notice.is_published ?? true,
          author: notice.author
        });
        if (editor) {
          editor.commands.setContent(notice.content);
        }
      }
    }
  }, [isEditMode, id, getNotice, editor]);

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-800 mb-2">
          {isEditMode ? '공지사항 수정' : '공지사항 등록'}
        </h1>
        <p className="text-gray-600">
          {isEditMode ? '공지사항 내용을 수정합니다.' : '새로운 공지사항을 등록합니다.'}
          <span className="text-red-500">*</span> 표시는 필수 항목입니다.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow p-8">
        {/* 기본 정보 */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">기본 정보</h2>

          <div className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                제목 <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="title"
                value={formData.title}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 ${
                  errors.title ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="공지사항 제목을 입력하세요"
              />
              {errors.title && (
                <p className="mt-1 text-sm text-red-500 flex items-center">
                  <ExclamationCircleIcon className="h-4 w-4 mr-1" />
                  {errors.title}
                </p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                담당부서 <span className="text-red-500">*</span>
              </label>
              <input
                type="text"
                name="department"
                value={formData.department}
                onChange={handleInputChange}
                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 ${
                  errors.department ? 'border-red-500' : 'border-gray-300'
                }`}
                placeholder="예: 교육지원과, 평생교육팀 등"
              />
              {errors.department && (
                <p className="mt-1 text-sm text-red-500 flex items-center">
                  <ExclamationCircleIcon className="h-4 w-4 mr-1" />
                  {errors.department}
                </p>
              )}
            </div>

            <div>
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="checkbox"
                  name="is_published"
                  checked={formData.is_published}
                  onChange={(e) => {
                    setFormData({
                      ...formData,
                      is_published: e.target.checked
                    });
                  }}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="text-sm font-medium text-gray-700">공개</span>
              </label>
              <p className="mt-1 text-xs text-gray-500 ml-6">체크하면 사용자에게 공개됩니다. 체크 해제 시 비공개로 설정됩니다.</p>
            </div>

            <div>
              <label className="flex items-center space-x-2 cursor-pointer">
                <input
                  type="checkbox"
                  name="is_important"
                  checked={formData.is_important}
                  onChange={(e) => {
                    setFormData({
                      ...formData,
                      is_important: e.target.checked,
                      // 체크 해제시 마감기한도 초기화
                      notice_end_date: e.target.checked ? formData.notice_end_date : null
                    });
                    // 체크 해제시 에러도 초기화
                    if (!e.target.checked && errors.notice_end_date) {
                      setErrors({ ...errors, notice_end_date: '' });
                    }
                  }}
                  className="w-4 h-4 text-green-600 border-gray-300 rounded focus:ring-green-500"
                />
                <span className="text-sm font-medium text-gray-700">공지글로 설정</span>
              </label>
              <p className="mt-1 text-xs text-gray-500 ml-6">체크하면 공지사항 목록 상단에 고정됩니다.</p>

              {/* 공지 마감기한 입력 필드 (조건부 표시) */}
              {formData.is_important && (
                <div className="mt-4 ml-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    📅 공지 마감기한 <span className="text-gray-500 text-xs font-normal">(선택사항)</span>
                  </label>
                  <input
                    type="date"
                    name="notice_end_date"
                    value={formData.notice_end_date || ''}
                    onChange={(e) => {
                      setFormData({ ...formData, notice_end_date: e.target.value });
                      if (errors.notice_end_date) {
                        setErrors({ ...errors, notice_end_date: '' });
                      }
                    }}
                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500 ${
                      errors.notice_end_date ? 'border-red-500' : 'border-gray-300'
                    }`}
                  />
                  {errors.notice_end_date ? (
                    <p className="mt-1 text-sm text-red-500 flex items-center">
                      <ExclamationCircleIcon className="h-4 w-4 mr-1" />
                      {errors.notice_end_date}
                    </p>
                  ) : (
                    <div className="mt-2 space-y-1">
                      <p className="text-xs text-gray-600">
                        • 마감기한을 설정하면 해당 날짜까지 공지사항 상단에 고정됩니다.
                      </p>
                      <p className="text-xs text-blue-700 font-medium">
                        • 마감기한을 설정하지 않으면 계속 공지로 표시됩니다.
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                내용 <span className="text-red-500">*</span>
              </label>
              <div className={`border rounded-lg ${errors.content ? 'border-red-500' : 'border-gray-300'} bg-white`}>
                {/* 에디터 툴바 - Reddit Style */}
                <div className="flex items-center gap-0.5 p-2 border-b border-gray-200">
                  <button
                    type="button"
                    onClick={() => editor?.chain().focus().toggleBold().run()}
                    className={`p-2 rounded hover:bg-gray-100 transition-colors ${
                      editor?.isActive('bold') ? 'bg-gray-200 text-gray-900' : 'text-gray-600'
                    }`}
                    title="Bold"
                  >
                    <BoldIcon className="h-5 w-5" />
                  </button>
                  <button
                    type="button"
                    onClick={() => editor?.chain().focus().toggleItalic().run()}
                    className={`p-2 rounded hover:bg-gray-100 transition-colors ${
                      editor?.isActive('italic') ? 'bg-gray-200 text-gray-900' : 'text-gray-600'
                    }`}
                    title="Italic"
                  >
                    <ItalicIcon className="h-5 w-5" />
                  </button>

                  <div className="w-px h-6 bg-gray-300 mx-1"></div>

                  <button
                    type="button"
                    onClick={() => editor?.chain().focus().toggleHeading({ level: 2 }).run()}
                    className={`px-2.5 py-1.5 rounded hover:bg-gray-100 transition-colors font-semibold text-sm ${
                      editor?.isActive('heading', { level: 2 }) ? 'bg-gray-200 text-gray-900' : 'text-gray-600'
                    }`}
                    title="Heading 2"
                  >
                    H2
                  </button>
                  <button
                    type="button"
                    onClick={() => editor?.chain().focus().toggleHeading({ level: 3 }).run()}
                    className={`px-2.5 py-1.5 rounded hover:bg-gray-100 transition-colors font-semibold text-sm ${
                      editor?.isActive('heading', { level: 3 }) ? 'bg-gray-200 text-gray-900' : 'text-gray-600'
                    }`}
                    title="Heading 3"
                  >
                    H3
                  </button>

                  <div className="w-px h-6 bg-gray-300 mx-1"></div>

                  <button
                    type="button"
                    onClick={() => editor?.chain().focus().toggleBulletList().run()}
                    className={`p-2 rounded hover:bg-gray-100 transition-colors ${
                      editor?.isActive('bulletList') ? 'bg-gray-200 text-gray-900' : 'text-gray-600'
                    }`}
                    title="Bullet List"
                  >
                    <ListBulletIcon className="h-5 w-5" />
                  </button>
                  <button
                    type="button"
                    onClick={() => editor?.chain().focus().toggleOrderedList().run()}
                    className={`p-2 rounded hover:bg-gray-100 transition-colors ${
                      editor?.isActive('orderedList') ? 'bg-gray-200 text-gray-900' : 'text-gray-600'
                    }`}
                    title="Numbered List"
                  >
                    <NumberedListIcon className="h-5 w-5" />
                  </button>

                  <div className="w-px h-6 bg-gray-300 mx-1"></div>

                  <button
                    type="button"
                    onClick={addImageFromButton}
                    className="p-2 rounded hover:bg-gray-100 transition-colors text-gray-600"
                    title="Insert Image"
                  >
                    <PhotoIcon className="h-5 w-5" />
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                  />
                </div>
                {/* 에디터 본문 */}
                <EditorContent
                  editor={editor}
                  className="prose max-w-none p-4 min-h-[300px] focus:outline-none"
                />
              </div>
              {errors.content && (
                <p className="mt-1 text-sm text-red-500 flex items-center">
                  <ExclamationCircleIcon className="h-4 w-4 mr-1" />
                  {errors.content}
                </p>
              )}
            </div>
          </div>
        </div>

        {/* 버튼 */}
        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={() => navigate('/admin/notices')}
            className="px-6 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
          >
            취소
          </button>
          <button
            type="submit"
            className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
          >
            {isEditMode ? '수정하기' : '등록하기'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default NoticeForm;
